import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lightbulb, AlertCircle, ArrowRight, Heart, Info, Globe, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { generateHealthInsights } from '@/utils/healthInsightGenerator';
import RedFlagAlert from '@/components/RedFlagAlert';
import healthApi from '@/api/healthApi';

const HealthInsight = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const checkInData = location.state?.checkInData;
  
  const [insights, setInsights] = useState(null);
  const [culturalInsight, setCulturalInsight] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadInsights = useCallback(async () => {
    if (!checkInData) {
      navigate('/quick-check');
      return;
    }

    try {
      setLoading(true);

      // Generate local insights with red flag detection
      const generated = generateHealthInsights(checkInData);
      setInsights(generated);

      // Try to fetch culturally adapted insight from backend
      try {
        const culturalResponse = await healthApi.getCulturalExamples();
        if (culturalResponse.data.success) {
          setCulturalInsight({
            region: culturalResponse.data.data.region_name,
            examples: culturalResponse.data.data.examples
          });
        }
      } catch (error) {
        // Cultural insights are optional - continue without them
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching cultural insights:', error);
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading insights:', error);
      }
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, [checkInData, navigate]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  if (loading || !insights) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Analyzing your check-in...</p>
        </div>
      </div>
    );
  }

  // Generate simple insights based on check-in data
  const symptoms = checkInData.symptoms || [];
  const habits = checkInData.habits || {};
  const stress = checkInData.stress_level;
  const energy = checkInData.energy_level;

  // Section 1: What body is signaling
  const bodySignals = [];
  const influencingFactors = [];

  if (symptoms.includes('tired') || energy === 'low') {
    bodySignals.push('You may be experiencing fatigue linked to poor rest');
    if (!habits.sleep) influencingFactors.push('Poor sleep quality');
  }

  if (!habits.water) {
    bodySignals.push('There are signs of mild dehydration');
    influencingFactors.push('Low water intake');
  }

  if (symptoms.includes('stressed') || stress === 'high' || stress === 'moderate') {
    bodySignals.push('Your body may be showing early stress buildup');
    influencingFactors.push('Elevated stress level');
  }

  if (symptoms.includes('headache')) {
    bodySignals.push('You may have tension or dehydration-related discomfort');
    if (!habits.water) influencingFactors.push('Insufficient hydration');
  }

  if (symptoms.includes('body_aches')) {
    bodySignals.push('Your muscles may need rest or movement');
    if (energy === 'low') influencingFactors.push('Low energy levels');
  }

  // If feeling okay
  if (symptoms.includes('okay') && bodySignals.length === 0) {
    bodySignals.push('You\'re managing your health well today');
    influencingFactors.push('Good self-care habits');
  }

  // Section 2: Simple steps
  const actionSteps = [];

  if (!habits.water) {
    actionSteps.push('Drink 2–3 glasses of water over the next hour');
  }

  if (!habits.sleep || symptoms.includes('tired')) {
    actionSteps.push('Take a short rest (20–30 minutes)');
  }

  if (stress === 'high' || stress === 'moderate') {
    actionSteps.push('Reduce screen time for a while');
  }

  if (energy === 'low' || !habits.movement) {
    actionSteps.push('Try light movement or fresh air');
  }

  if (culturalInsight) {
    // Add cultural activity suggestion
    const activity = culturalInsight.examples.wellness_practices?.[0];
    if (activity && !actionSteps.includes(activity)) {
      actionSteps.push(`Try ${activity} for relaxation`);
    }
  }

  // Default actions if none generated
  if (actionSteps.length === 0) {
    actionSteps.push('Continue your healthy habits');
    actionSteps.push('Stay mindful of how you feel');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 pb-20">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        
        {/* Red Flag Alert - Show FIRST if triggered */}
        {insights.hasRedFlag && (
          <RedFlagAlert 
            symptoms={checkInData.additional_symptom} 
            onClose={() => {}}
          />
        )}

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full p-3">
              <Lightbulb className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Your Health Insight
          </h1>
          <p className="text-gray-600">Based on today's check-in</p>
        </div>

        {/* Section 1: What your body may be signalling */}
        <Card className="border-2 border-purple-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              What your body may be signalling
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Your responses suggest:</p>
              <ul className="space-y-2">
                {bodySignals.map((signal, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-purple-600 mt-0.5">•</span>
                    <span>{signal}</span>
                  </li>
                ))}
              </ul>
            </div>

            {influencingFactors.length > 0 && (
              <div className="pt-3 border-t">
                <p className="text-sm font-semibold text-gray-700 mb-2">This is likely influenced by:</p>
                <ul className="space-y-2">
                  {influencingFactors.map((factor, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-blue-600 mt-0.5">•</span>
                      <span>{factor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 2: Simple steps to support your body */}
        <Card className="border-2 border-blue-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Heart className="h-5 w-5 text-blue-600" />
              Simple steps to support your body
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {actionSteps.map((step, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200"
              >
                <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                  {index + 1}
                </div>
                <p className="text-sm text-gray-700 flex-1">{step}</p>
              </div>
            ))}

            {/* Tip */}
            <div className="mt-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-yellow-800">💡 Tip: </span>
                Start small — even one step makes a difference today
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Cultural Insight - If available */}
        {culturalInsight && (
          <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
            <CardContent className="pt-5">
              <div className="flex items-start gap-3">
                <Globe className="h-6 w-6 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-purple-900 mb-2">
                    Culturally Relevant Suggestions ({culturalInsight.region})
                  </p>
                  <div className="space-y-2 text-sm text-purple-800">
                    {culturalInsight.examples.beverages && culturalInsight.examples.beverages.length > 0 && (
                      <p>
                        💧 <strong>Stay hydrated:</strong> {culturalInsight.examples.beverages.slice(0, 2).join(', ')}
                      </p>
                    )}
                    {culturalInsight.examples.activities && culturalInsight.examples.activities.length > 0 && (
                      <p>
                        🏃 <strong>Move your body:</strong> {culturalInsight.examples.activities.slice(0, 2).join(', ')}
                      </p>
                    )}
                    {culturalInsight.examples.wellness_practices && culturalInsight.examples.wellness_practices.length > 0 && (
                      <p>
                        🧘 <strong>Manage stress:</strong> {culturalInsight.examples.wellness_practices.slice(0, 2).join(', ')}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => navigate('/cultural-settings')}
                    className="text-xs text-purple-600 hover:text-purple-700 mt-2 underline"
                  >
                    Change region preferences
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 3: When to seek support */}
        <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Info className="h-5 w-5 text-amber-600" />
              When to seek support
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm font-semibold text-gray-700">Consider seeking medical advice if:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-amber-600 mt-0.5">•</span>
                <span>Symptoms persist for more than 2–3 days</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-amber-600 mt-0.5">•</span>
                <span>You feel worse instead of better</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-amber-600 mt-0.5">•</span>
                <span>New or unusual symptoms appear</span>
              </li>
            </ul>

            <div className="pt-3 border-t border-amber-200">
              <p className="text-sm text-gray-700 mb-3">
                If concerned, contact <strong>NHS 111</strong> or your <strong>GP</strong>
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-2 border-amber-300 hover:bg-amber-100"
                  onClick={() => window.open('https://111.nhs.uk/', '_blank')}
                >
                  NHS 111 Online
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Button */}
        <Button
          onClick={() => navigate('/daily-loop')}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
        >
          Continue My Daily Health Loop
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>

        {/* Secondary Actions */}
        <div className="flex gap-3">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="flex-1 border-2 border-gray-300 hover:bg-gray-50"
          >
            Back to Home
          </Button>
          <Button
            onClick={() => navigate('/quick-check')}
            variant="outline"
            className="flex-1 border-2 border-purple-300 hover:bg-purple-50"
          >
            New Check-in
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HealthInsight;
